import { computed, toValue } from "vue";
import { useQuery } from "@tanstack/vue-query";

import { queryKeys } from "@/queries/_shared/queryKeys";
import { noCacheHeaders, noCacheQueryOptions } from "@/queries/_shared/noCache";
import http from "@/services/http";

function unwrapBody(data) {
  if (data && typeof data === "object" && "body" in data) return data.body;
  return data;
}

async function fetchScheduleRaspisanie(ts, groupId, pupilId) {
  const response = await http.get("/scheduler/raspisanie", {
    headers: noCacheHeaders,
    params: {
      ts: Number(ts || 0),
      group_id: Number(groupId || 0),
      pupil_id: Number(pupilId || 0),
    },
  });
  return unwrapBody(response.data) || [];
}

async function fetchScheduleBells() {
  const response = await http.get("/scheduler/time-profiles", {
    headers: noCacheHeaders,
    params: {
      'expand':'bells,days'
    },
  });
  return unwrapBody(response.data) || [];
}

async function fetchSchedulePlans(startTs, endTs, groupId) {
  const response = await http.get("/lplans", {
    params: {
      start_ts: Number(startTs || 0),
      end_ts: Number(endTs || 0),
      group_id: Number(groupId || 0),
      expand: "hometask",
      "per-page": 1000,
    },
  });
  return unwrapBody(response.data) || [];
}

async function fetchScheduleMarks(startTs, endTs, pupilId) {
  const response = await http.get("/marks/current", {
    params: {
      start_ts: Number(startTs || 0),
      end_ts: Number(endTs || 0),
      expand: "lplan,parent_response",
      p_id: Number(pupilId || 0),
      "per-page": 1000,
    },
  });
  return unwrapBody(response.data) || [];
}

export function useScheduleRaspisanieQuery(ts, groupId, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() =>
      queryKeys.schedule.raspisanie(toValue(ts), toValue(groupId), toValue(pupilId))
    ),
    queryFn: () =>
      fetchScheduleRaspisanie(toValue(ts), toValue(groupId), toValue(pupilId)),
    enabled: computed(
      () =>
        Number(toValue(ts) || 0) > 0 &&
        Number(toValue(groupId) || 0) > 0 &&
        Number(toValue(pupilId) || 0) > 0
    ),
    ...options,
    ...noCacheQueryOptions,
  });
}

export function useScheduleBellsQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.schedule.bells(),
    queryFn: fetchScheduleBells,
    ...options,
    ...noCacheQueryOptions,
  });
}

export function useSchedulePlansQuery(startTs, endTs, groupId, options = {}) {
  return useQuery({
    queryKey: computed(() =>
      queryKeys.schedule.plans(toValue(startTs), toValue(endTs), toValue(groupId))
    ),
    queryFn: () => fetchSchedulePlans(toValue(startTs), toValue(endTs), toValue(groupId)),
    enabled: computed(
      () =>
        Number(toValue(startTs) || 0) > 0 &&
        Number(toValue(endTs) || 0) > 0 &&
        Number(toValue(groupId) || 0) > 0
    ),
    ...options,
  });
}

export function useScheduleMarksQuery(startTs, endTs, pupilId, options = {}) {
  return useQuery({
    queryKey: computed(() =>
      queryKeys.schedule.marks(toValue(startTs), toValue(endTs), toValue(pupilId))
    ),
    queryFn: () => fetchScheduleMarks(toValue(startTs), toValue(endTs), toValue(pupilId)),
    enabled: computed(
      () =>
        Number(toValue(startTs) || 0) > 0 &&
        Number(toValue(endTs) || 0) > 0 &&
        Number(toValue(pupilId) || 0) > 0
    ),
    ...options,
  });
}

export {
  fetchScheduleBells,
  fetchScheduleMarks,
  fetchSchedulePlans,
  fetchScheduleRaspisanie,
};
